/**
 * The inbox card (§0, §8.2) — self-contained the way `AreasCard` is: its own fetch of
 * `/api/inbox`, its own areas/projects for the destination pickers, its own resolve handler.
 *
 * `InboxSection` stays presentational and does the actual rendering/forms; this just feeds
 * it data and wraps `api.resolveInbox` the way `Briefing.tsx` used to inline it — including
 * the two-step recurrence dance (`setRecurrence` needs the task's id, which only exists once
 * resolving has actually produced one) and the fact that resolving can create an area or
 * project, which is why this reloads its own areas/projects alongside the inbox list rather
 * than just the list.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  api,
  type CaptureTier,
  type InboxAiRetryResult,
  type InboxItem,
  type InboxRefineBody,
  type RecurrenceRequest,
  type Task,
} from "../api";
import { useAreasAndProjects } from "../areasProjects";
import { useEditMode } from "../layout/editModeContext";
import { Placeholder } from "../layout/LayoutGrid";
import { postNotice } from "../notices";
import { queueInboxResolve, type InboxResolveBody } from "../taskQueue";
import { InboxSection, type ResolveRequest } from "./InboxSection";

/** How often the card re-reads the Inbox while the AI has items queued, to notice one being
 * picked up and one finishing. The worker itself only looks every ~20 seconds. */
const POLL_MS = 2500;

const STALE_ROW =
  "That item has changed since the list loaded, so it's been refreshed. Try the lightbulb again.";

const ULID = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

/**
 * Oldest entry first, whatever the rows were read from.
 *
 * `created_at` is when the *server* made the row, and for an entry the Add card holds back for a
 * position fix that is up to several seconds after the person pressed Add — so a second entry,
 * shown from the saved copy at its own press time, sat above the first until the server's copy
 * arrived and the two swapped. The id is minted when Add is pressed and the server keeps it, so
 * ids put both sources in the same order. (A row whose id is not a ULID falls back to
 * `created_at`.)
 */
function byEntryTime(a: InboxItem, b: InboxItem): number {
  if (ULID.test(a.id) && ULID.test(b.id)) return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  return a.created_at - b.created_at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/** How long to wait for the answer to an item the AI was seen working on before giving up on
 * hearing it — a restart mid-call leaves the "working" mark behind with nobody to clear it. */
const ANSWER_TIMEOUT_MS = 150_000;

export function InboxCard({
  refreshKey,
  onChange,
}: {
  /** Bumped whenever something elsewhere changed, so a newly-touched area shows up in the
   * destination picker without this card needing to know why. */
  refreshKey?: number;
  /** Called after resolving an item, so siblings relying on the same areas/projects/tasks
   * (Areas, Wins, Tasks, wherever they live) know to refetch too. */
  onChange?: () => void | Promise<void>;
}) {
  const { areas, projects, reload: reloadDestinations } = useAreasAndProjects(refreshKey);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { editing } = useEditMode();
  // Items an AI retry is still showing the answer for. A filed one is resolved on the server
  // and so absent from the next fetch, but its row has to outlive that until the person has
  // seen where it went — and anything else on the page can trigger a reload in the meantime.
  const held = useRef(new Map<string, InboxItem>());

  // The Queue (#82). `seen` is the queued/processing items from the last read: an item that
  // was in it and no longer is, or has been tried again, is one the worker just finished — and
  // since no request carried its answer back, `results` is where it is fetched to. `manual`
  // are items this person's own bulb is running, whose answer arrives on that request instead.
  const seen = useRef(new Map<string, InboxItem>());
  const results = useRef(new Map<string, InboxAiRetryResult>());
  const waiters = useRef(
    new Map<string, { resolve: (r: InboxAiRetryResult) => void; reject: (e: Error) => void }>(),
  );
  const manual = useRef(new Set<string>());
  // Which section each held row stays in, and which queue rows follow the worker in the bulb.
  const [pins, setPins] = useState<ReadonlyMap<string, "queue" | "unresolved">>(new Map());
  const [watching, setWatching] = useState<ReadonlySet<string>>(new Set());

  const pin = useCallback((item: InboxItem, section: "queue" | "unresolved") => {
    setPins((current) => new Map(current).set(item.id, section));
  }, []);
  const release = useCallback((id: string) => {
    held.current.delete(id);
    manual.current.delete(id);
    results.current.delete(id);
    waiters.current.delete(id);
    setPins((current) => {
      if (!current.has(id)) return current;
      const next = new Map(current);
      next.delete(id);
      return next;
    });
    setWatching((current) => {
      if (!current.has(id)) return current;
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  const loadRef = useRef<() => Promise<void>>(async () => {});

  /** The worker has finished with `prev`: keep its row, then read how it went. */
  const collectResult = useCallback(
    async (prev: InboxItem) => {
      held.current.set(prev.id, prev);
      pin(prev, "queue");
      try {
        const result = await api.inboxResult(prev.id);
        results.current.set(prev.id, result);
        setWatching((current) => new Set(current).add(prev.id));
        waiters.current.get(prev.id)?.resolve(result);
      } catch {
        // Nothing the AI did to report (it was dismissed, say): the row just goes.
        waiters.current.get(prev.id)?.reject(new Error("Couldn't read how that went."));
        release(prev.id);
        await loadRef.current();
      }
    },
    [pin, release],
  );

  const load = useCallback(async () => {
    try {
      const fetched = (await api.inbox()).items;
      const byId = new Map(fetched.map((item) => [item.id, item]));
      for (const [id, prev] of seen.current) {
        if (results.current.has(id) || manual.current.has(id)) continue;
        const now = byId.get(id);
        const left =
          !now ||
          now.state === "unresolved" ||
          (now.state === "queued" && now.parse_attempts > prev.parse_attempts);
        if (left) void collectResult(prev);
      }
      seen.current = new Map(
        fetched
          .filter(
            (item) =>
              (item.state === "queued" || item.state === "processing") &&
              !manual.current.has(item.id),
          )
          .map((item) => [item.id, item]),
      );
      const kept = [...held.current.values()].filter((item) => !byId.has(item.id));
      setItems([...fetched, ...kept].sort(byEntryTime));
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [collectResult]);
  loadRef.current = load;

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  // While anything is queued, keep reading — otherwise a pickup or a finish would only show
  // when something else happened to refresh the card.
  const queuedNow = items.some((item) => item.state === "queued" || item.state === "processing");
  useEffect(() => {
    if (!queuedNow) return;
    // Not gated on the tab being visible: some windows report themselves hidden while on
    // screen, and this only runs while something is queued, which is seconds.
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [queuedNow, load]);

  const resolve = useCallback(
    async (item: InboxItem, request: ResolveRequest, recurrence?: RecurrenceRequest) => {
      try {
        const result = await api.resolveInbox(item.id, request);
        if (recurrence && result.task && typeof result.task === "object") {
          await api.setRecurrence((result.task as Task).id, recurrence);
        }
      } catch (err) {
        if (!(err instanceof ApiError && err.isOffline)) throw err;
        if (recurrence) {
          // Recurrence is a second call keyed on the id the first one returns, and the queue
          // does not chain replayed operations (D154, D157) — refused rather than filed with
          // the repeat silently dropped.
          postNotice(
            "Setting a repeat needs a connection. File this without it, or try again online.",
          );
          return;
        }
        await queueInboxResolve(item.id, request as Omit<InboxResolveBody, "id">);
        postNotice("Filed - it'll reach the server once you're back online.");
      }
      await Promise.all([load(), reloadDestinations()]);
      await onChange?.();
    },
    [load, reloadDestinations, onChange],
  );

  const retryAi = useCallback(
    (item: InboxItem, tier: CaptureTier | null) => {
      held.current.set(item.id, item);
      manual.current.add(item.id);
      seen.current.delete(item.id);
      pin(item, item.state === "queued" || item.state === "processing" ? "queue" : "unresolved");
      return api.retryInboxAi(item.id, tier).catch((err: unknown) => {
        // A row the server has never heard of is a stale one: reload so the real one replaces it.
        if (err instanceof ApiError && err.status === 404) {
          void loadRef.current();
          throw new Error(STALE_ROW);
        }
        throw err;
      });
    },
    [pin],
  );

  /** The worker's answer for an item it is on (or has just finished), once it has one. */
  const awaitResult = useCallback(
    (item: InboxItem) => {
      held.current.set(item.id, item);
      pin(item, "queue");
      setWatching((current) => new Set(current).add(item.id));
      const known = results.current.get(item.id);
      if (known) return Promise.resolve(known);
      return new Promise<InboxAiRetryResult>((resolve, reject) => {
        const timer = window.setTimeout(() => {
          waiters.current.delete(item.id);
          reject(new Error("The AI didn't answer in time. It's back in the queue."));
        }, ANSWER_TIMEOUT_MS);
        waiters.current.set(item.id, {
          resolve: (result) => {
            window.clearTimeout(timer);
            resolve(result);
          },
          reject: (error) => {
            window.clearTimeout(timer);
            reject(error);
          },
        });
      });
    },
    [pin],
  );

  const refineAi = useCallback(
    async (item: InboxItem, body: InboxRefineBody, recurrence?: RecurrenceRequest) => {
      held.current.set(item.id, item);
      manual.current.add(item.id);
      pin(item, "unresolved");
      let result: InboxAiRetryResult;
      try {
        result = await api.refineInboxAi(item.id, body);
      } catch (err) {
        if (err instanceof ApiError && err.isOffline) {
          // Create-then-resolve: the outbox does not chain replayed calls (D154, D157), and
          // there is no answer to wait for offline anyway.
          throw new Error(
            "Asking the AI needs a connection. Try again once you're back online.",
          );
        }
        if (err instanceof ApiError && err.status === 404) {
          void loadRef.current();
          throw new Error(STALE_ROW);
        }
        throw err;
      }
      if (recurrence && result.created?.kind === "task") {
        // Recurrence is a second call keyed on the task's id, which only exists now.
        try {
          await api.setRecurrence(result.created.id, recurrence);
        } catch (err) {
          // The task is made and the item resolved; undoing either would be worse than saying so.
          postNotice(
            "The task was created, but its repeat couldn't be set" +
              (err instanceof Error ? `: ${err.message}` : "."),
          );
        }
      }
      return result;
    },
    [pin],
  );

  const retryAiSettled = useCallback(
    async (item: InboxItem, result: InboxAiRetryResult | null) => {
      release(item.id);
      await load();
      // Whatever the AI filed is new to every other card, which has been kept waiting so that
      // this one's refresh could not pull the held row out from under the message.
      if (result?.outcome === "filed") await onChange?.();
    },
    [load, onChange, release],
  );

  if (items.length === 0 && !error) return editing ? <Placeholder id="inbox" /> : null;

  return (
    <>
      {error && (
        <p className="error" style={{ margin: "0 0 0.6rem" }}>
          {error}
        </p>
      )}
      <InboxSection
        items={items}
        areas={areas}
        projects={projects}
        onResolve={resolve}
        onRetryAi={retryAi}
        onRefineAi={refineAi}
        onRetryAiSettled={retryAiSettled}
        onAwaitResult={awaitResult}
        pinned={pins}
        watching={watching}
        standalone
      />
    </>
  );
}
